import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MetaService} from '../../../../meta/service/meta.service';
import {CoreMetaExternalSideCarService} from '../../../../meta/service/core-meta-external-side-car.service';
import {MetaDataEntityDto} from '../../../../meta/dto/meta-data-entity.dto';
import {MetaDataAttributeDto} from '../../../../meta/dto/meta-data-attribute.dto';
import {MetaDataAttributeKind} from '../../../../meta/logic/meta-data-attribute-kind';
import {MetaDataJoinDto} from '../../../../meta/dto/meta-data-join.dto';
import {MetaEntityRequestDto} from '../../../../meta/dto/meta-entity-request.dto';
import {MetaDataJoinRelationEnum} from '../../../../meta/enum/meta-data-join-relation.enum';
import {CoreMetaExternalSideCarDto} from '../../../../meta/dto/meta-external-side-car.dto';
import {CoreMetaExternalSideCarFieldDto} from '../../../../meta/dto/meta-external-side-car-field.dto';
import {FilterCriteria} from '../../../../filter/api/filter.criteria';
import {FilterCriteriaChangeEvent} from '../../../../filter/api/filter-criteria-change-event';
import {WidgetData} from '../../../../widget/widget-data';
import {
    QlBuilderExtendedNode,
    QlBuilderFieldModel,
    QlBuilderJoinNode,
    QlBuilderJoinRelation,
    QlBuilderJoinType,
    QlBuilderSideCarNode,
} from '../../../builder/ql-builder-model';
import {QL_BUILDER_POLICY_LIMITS, QlBuilderFieldPatterns, QlBuilderQueryConverter} from '../../../builder/ql-builder-query-converter';
import {QlBuilderContainerNode, QlBuilderNodeContext, QlBuilderTreeOps} from '../logic/ql-builder-tree-ops';
import {QlBuilderFieldOption} from '../interface/ql-builder-field-option.interface';
import {QlBuilderJoinMenuItem} from '../interface/ql-builder-join-menu-item.interface';
import {QlBuilderJoinMenuSection, QlBuilderJoinMenuSectionKey} from '../interface/ql-builder-join-menu-section.interface';
import {QlBuilderSideCarFieldOption} from '../interface/ql-builder-side-car-field-option.interface';
import {QlBuilderSideCarDraft} from '../interface/ql-builder-side-car-draft.interface';
import {QlBuilderSideCarInfo} from '../interface/ql-builder-side-car-info.interface';
import {QlBuilderKeyLabels} from '../logic/ql-builder-key-labels';
import {QlBuilderRelativeFilterChip} from '../interface/ql-builder-relative-filter-chip.interface';
import {QlBuilderRelativeDateDraft} from '../interface/ql-builder-relative-date-draft.interface';
import {QlBuilderRelativeDate} from '../../../builder/ql-builder-relative-date';

/** Field-selection section of a node. */
export type QlBuilderFieldSection = 'regular' | 'generic' | 'eav';

/** Display info of a child node, derived from this node's metadata. */
interface QlBuilderChildInfo {
    /** Entity alias of a regular join child, `null` if it cannot be resolved. */
    entityAlias: string | null;
    title: string;
}

/**
 * One node of the QL-Widget-Builder tree (recursive): the start entity or a regular join. Shows the
 * field sections ("Felder", "Generische Felder", "EAV-Felder"), the filter dialog, the "Relativer
 * Zeitraum" editor for date fields (`${D-…}` filter values as chips), the child nodes
 * and the "Verknüpfung hinzufügen" dialog: a wide modal with a live search over label AND technical
 * name and the four categories "Verknüpft (n:1)", "Zugehörige (1:n)", "Erweiterte Felder (berechnet)"
 * and "Zusatzfelder (extern)" as tabs (one scroll area, a counter per tab, disabled entries stay visible
 * with their reason as text). A reverse join (`<alias>#<fk>`, label = alias on the wire) gets the entity
 * label of its target from the metadata (one bulk `MetaService.list` on first open) plus "über Feld <fk>"
 * as its relation. The side-car flow (version → fields) runs inside the same dialog.
 * Extended and side-car children are leaves and rendered inline as cards; regular join children recurse
 * into this component.
 *
 * Every edit emits a NEW node object through {@link nodeChange} (the parent replaces its child, the
 * panel replaces the root and emits a new model) — the input is never mutated.
 *
 * Declared in `QlBuilderModule` (NgModule, fe_core_standards) next to the panel and the relative-date
 * editor, without touching `shared.module.ts`; the recursion (`mvs-ql-builder-node` inside its own
 * template) resolves through that module's compilation scope.
 */
@Component({
    selector: 'mvs-ql-builder-node',
    templateUrl: './ql-builder-node.component.html',
    styleUrls: ['./ql-builder-node.component.scss'],
    standalone: false,
})
export class QlBuilderNodeComponent implements OnChanges, OnDestroy {

    static readonly TEXT_NOT_UNIQUE =
        'Werte bei Duplikaten nicht eindeutig: Liegen je Datensatz mehrere Werte vor, wird ein beliebiger davon angezeigt.';

    static readonly TEXT_NOT_PIVOTABLE =
        'Dieser Feldtyp (Objekt/Struktur/JSON) kann nicht als Spalte ausgegeben werden.';

    static readonly TEXT_EXTENDED_GROUP_BY =
        'Im Gruppieren-Modus nicht möglich: erweiterte Felder werden pro Zeile berechnet und lassen sich nicht gruppieren.';

    static readonly TEXT_SIDE_CAR_GROUP_BY =
        'Im Gruppieren-Modus nicht möglich: externe Zusatzfelder lassen sich nicht gruppieren.';

    static readonly TEXT_EXTENDED_WITH_SIDE_CAR =
        'Nicht möglich, solange externe Zusatzfelder in der Definition sind – beides lässt sich nicht kombinieren.';

    static readonly TEXT_SIDE_CAR_WITH_EXTENDED =
        'Nicht möglich, solange ein erweitertes Feld in der Definition ist – beides lässt sich nicht kombinieren.';

    static readonly TEXT_EXTENDED_MAX =
        'Nur ein erweitertes Feld je Definition möglich – es wird pro Zeile berechnet.';

    static readonly TEXT_ONLY_FIRST_NO_SORT =
        'Mit „nur erster Eintrag“ ist diese Verknüpfung nicht sortierbar: '
        + 'welcher Eintrag geliefert wird, ist nicht festgelegt.';

    static readonly TEXT_GENERIC_OBJECT_GROUP_BY =
        'Objekt-Felder (generisch) lassen sich nicht gruppieren – nur einfache generische Felder.';

    static readonly TEXT_RELATIVE_DATE =
        'Filter auf ein Datumsfeld relativ zu heute, Monats- oder Jahresanfang/-ende – z. B. „die letzten 30 Tage“.';

    /** The node rendered by this instance (root entity node or a regular join node). */
    @Input() node: QlBuilderContainerNode;

    /** Entity alias of this node (root: start entity; join: the join target), `null` if unresolvable. */
    @Input() entityAlias: string | null = null;

    /** Header title of a join node (meta label of the join); the root shows the entity label. */
    @Input() title: string | null = null;

    @Input() context: QlBuilderNodeContext;

    @Input() isRoot: boolean = false;

    @Output() nodeChange = new EventEmitter<QlBuilderContainerNode>();

    /** A join node asks its parent to remove it. */
    @Output() remove = new EventEmitter<void>();

    readonly limits = QL_BUILDER_POLICY_LIMITS;
    readonly textNotUnique = QlBuilderNodeComponent.TEXT_NOT_UNIQUE;
    readonly textOnlyFirstNoSort = QlBuilderNodeComponent.TEXT_ONLY_FIRST_NO_SORT;
    readonly textGenericObjectGroupBy = QlBuilderNodeComponent.TEXT_GENERIC_OBJECT_GROUP_BY;
    readonly textRelativeDate = QlBuilderNodeComponent.TEXT_RELATIVE_DATE;

    readonly joinTypeOptions: { label: string; value: QlBuilderJoinType }[] = [
        {label: 'Optional (left)', value: 'left'},
        {label: 'Pflicht (inner)', value: 'inner'},
    ];

    meta: MetaDataEntityDto | null = null;
    metaState: 'none' | 'loading' | 'ready' | 'error' = 'none';

    sideCars: CoreMetaExternalSideCarDto[] | null = null;
    sideCarsState: 'none' | 'loading' | 'ready' | 'error' = 'none';

    regularOptions: QlBuilderFieldOption[] = [];
    genericOptions: QlBuilderFieldOption[] = [];
    eavOptions: QlBuilderFieldOption[] = [];

    /** Date-typed fields of this node's entity — the only fields a relative date may filter (backend policy). */
    dateFieldOptions: QlBuilderFieldOption[] = [];

    /** The node's relative date filters, shown as chips instead of in the filter summary. */
    relativeFilterChips: QlBuilderRelativeFilterChip[] = [];

    /** The open "Relativer Zeitraum" editor, `null` when closed. */
    relativeDateDraft: QlBuilderRelativeDateDraft | null = null;

    /** Grouping mode hides the object-typed generic fields of this entity (spike S3) — shown as a hint. */
    objectGenericsHiddenByGroupBy: boolean = false;

    selected: { [section in QlBuilderFieldSection]: string[] } = {regular: [], generic: [], eav: []};

    menuSections: QlBuilderJoinMenuSection[] = [];

    /** Context handed to child nodes — recomputed with the node, so children only see a new object on a real change. */
    childContext: QlBuilderNodeContext | null = null;

    /** Child join display info, keyed by child alias. */
    childInfo: { [alias: string]: QlBuilderChildInfo } = {};

    filterDialogVisible: boolean = false;
    filterDialogCriteria: FilterCriteria[] = [];

    /**
     * The `WidgetData` the hosted `mvs-condition-builder` reads (QA #1): `MvsFilterHelper.buildCascadeSelectNodes`
     * dereferences `widgetData.dataSource` unconditionally, and the shared `mvs-conditional-builder-entity` /
     * `-dialog` chain has no `widgetData` input at all — so the dialog opened empty with a `TypeError`. The node
     * therefore hosts the condition builder in its own dialog and hands it an entity-sourced `WidgetData`
     * (`dataSource: 'entity'`, `dataProviderObject: <entity alias>`), the branch that never reads a QL request.
     */
    filterDialogWidgetData: WidgetData | null = null;

    /** The condition builder's last change (criteria + validity), `null` until the user edits something. */
    filterDraft: { criteria: FilterCriteria[]; valid: boolean } | null = null;

    sideCarDraft: QlBuilderSideCarDraft | null = null;

    /**
     * Version + field labels per side-car child alias (N7): from the model when the side-car was applied in
     * this session, otherwise reloaded from the discovery (a saved definition carries only ids).
     */
    sideCarInfo: { [alias: string]: QlBuilderSideCarInfo } = {};

    /** The "Verknüpfung hinzufügen" dialog is open (list view, or the side-car flow while `sideCarDraft` is set). */
    joinDialogVisible: boolean = false;

    /** Live search text of the dialog — filters every category by label and technical name. */
    joinSearch: string = '';

    /** The active category tab of the dialog. */
    joinCategory: QlBuilderJoinMenuSectionKey = 'nToOne';

    /** {@link menuSections} with the search applied — what the dialog renders (never filtered in the template). */
    joinDialogSections: QlBuilderJoinMenuSection[] = [];

    private metaSubscription: Subscription | null = null;
    private sideCarSubscription: Subscription | null = null;
    private draftSubscription: Subscription | null = null;
    private loadedAlias: string | null = null;

    /** Discovery labels per side-car version (`QlBuilderKeyLabels.sideCarKey`), loaded once per key. */
    private readonly sideCarLabelCache = new Map<string, { scopeLabel: string | null; fieldLabels: Map<number, string> }>();
    private readonly sideCarLabelRequested = new Set<string>();
    private readonly sideCarLabelSubscriptions = new Subscription();

    /**
     * Label-only metadata (`entityLabel`) of join TARGET entities, `null` when the load failed or the alias is
     * unknown. Requested lazily on the first dialog open of this node, in one bulk request.
     */
    private readonly targetMetaCache = new Map<string, MetaDataEntityDto | null>();
    private readonly targetMetaRequested = new Set<string>();
    private readonly targetMetaSubscriptions = new Subscription();

    constructor(
        private readonly metaService: MetaService,
        private readonly sideCarService: CoreMetaExternalSideCarService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityAlias'] && this.entityAlias !== this.loadedAlias) {
            this.loadMeta();
        }
        // an edited filter that moved or vanished (another edit replaced the node) closes its editor
        const draft = this.relativeDateDraft;
        if (changes['node'] && draft?.index != null && this.node?.filters?.[draft.index] !== draft.criterion) {
            this.relativeDateDraft = null;
        }
        this.recompute();
    }

    ngOnDestroy(): void {
        this.metaSubscription?.unsubscribe();
        this.sideCarSubscription?.unsubscribe();
        this.draftSubscription?.unsubscribe();
        this.sideCarLabelSubscriptions.unsubscribe();
        this.targetMetaSubscriptions.unsubscribe();
    }

    // ------------------------------------------------------------------
    // derived view state
    // ------------------------------------------------------------------

    get joinNode(): QlBuilderJoinNode | null {
        return this.node?.kind === 'join' ? this.node : null;
    }

    /** Root in grouping mode: the columns come from the grouping editor of the panel. */
    get fieldsReplacedByGroupBy(): boolean {
        return this.isRoot && !!this.context?.groupByMode;
    }

    get fieldCount(): number {
        return this.node?.fields?.length ?? 0;
    }

    get headerTitle(): string {
        if (this.isRoot) {
            return this.meta?.entityLabel || this.entityAlias || this.node?.alias || '';
        }
        return this.title || this.node?.alias || '';
    }

    /** Remaining field budget of a section (policy: max 30 fields per node). */
    selectionLimit(section: QlBuilderFieldSection): number {
        const others = this.fieldCount - this.selected[section].length;
        return Math.max(0, QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode - others);
    }

    get addChildBlockedReason(): string | null {
        return this.context ? QlBuilderTreeOps.addChildBlockedReason(this.context) : null;
    }

    extendedChildren(): QlBuilderExtendedNode[] {
        return (this.node?.children ?? []).filter((child): child is QlBuilderExtendedNode => child.kind === 'extended');
    }

    sideCarChildren(): QlBuilderSideCarNode[] {
        return (this.node?.children ?? []).filter((child): child is QlBuilderSideCarNode => child.kind === 'sideCar');
    }

    joinChildren(): QlBuilderJoinNode[] {
        return (this.node?.children ?? []).filter((child): child is QlBuilderJoinNode => child.kind === 'join');
    }

    extendedLabel(child: QlBuilderExtendedNode): string {
        const name = child.name;
        const attribute = this.meta?.attributes?.find(a => QlBuilderNodeComponent.bareName(a.attributeName) === name);
        const join = this.meta?.joins?.find(j => QlBuilderNodeComponent.bareName(j.name) === name);
        return attribute?.label || join?.label || name;
    }

    /** The discovery row of a side-car child, `undefined` while unknown. */
    sideCarRow(child: QlBuilderSideCarNode): CoreMetaExternalSideCarDto | undefined {
        return this.sideCars?.find(row => row.joinName === child.instanceAlias || row.instanceAlias === child.instanceAlias);
    }

    /** Q-B: the hint "Werte bei Duplikaten nicht eindeutig" is shown for this side-car. */
    showNotUniqueHint(sideCar: CoreMetaExternalSideCarDto | null | undefined): boolean {
        return !!sideCar && sideCar.uniqueGuaranteed === false;
    }

    /**
     * Summary of the dialog filters, readable (N5): field labels, operator symbols, enum labels and dates as
     * `dd.MM.yyyy`; a day expanded into `[day, next day)` reads as one condition again. Relative date filters
     * are shown as chips instead.
     */
    filterSummary(): string {
        const prefix = `${this.node.alias}.`;
        const resolver = {
            fieldLabel: (field: string) => this.filterFieldLabel(field.startsWith(prefix) ? field.substring(prefix.length) : field),
            valueLabel: (field: string, value: unknown) =>
                this.filterValueLabel(field.startsWith(prefix) ? field.substring(prefix.length) : field, value),
        };
        return QlBuilderNodeComponent.foldDayRanges(this.dialogFilters())
            .map(criteria => QlBuilderNodeComponent.describeCriteria(criteria, resolver))
            .join(' · ');
    }

    /** Number of filters edited in the filter dialog (without the relative date chips; a day range counts once). */
    get dialogFilterCount(): number {
        return QlBuilderNodeComponent.foldDayRanges(this.dialogFilters()).length;
    }

    // ------------------------------------------------------------------
    // fields
    // ------------------------------------------------------------------

    onSectionChange(section: QlBuilderFieldSection, values: string[]): void {

        const existing = new Map<string, QlBuilderFieldModel>((this.node.fields ?? []).map(f => [f.name, f]));
        const next: { [s in QlBuilderFieldSection]: string[] } = {...this.selected, [section]: values ?? []};

        const fields: QlBuilderFieldModel[] = [...next.regular, ...next.generic, ...next.eav]
            .slice(0, QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode)
            .map(name => existing.get(name) ?? {name});

        this.emitNode({...this.node, fields});
    }

    // ------------------------------------------------------------------
    // join header (join nodes only)
    // ------------------------------------------------------------------

    onJoinTypeChange(joinType: QlBuilderJoinType): void {
        const join = this.joinNode;
        if (join) {
            this.emitNode({...join, joinType});
        }
    }

    /** With „nur erster Eintrag“ the join sort is dropped — it fails in the engine (spike S5). */
    onOnlyFirstChange(onlyFirst: boolean): void {
        const join = this.joinNode;
        if (!join) {
            return;
        }
        if (onlyFirst) {
            const {sortings, ...rest} = join;
            this.emitNode({...rest, onlyFirst: true});
        } else {
            this.emitNode({...join, onlyFirst: false});
        }
    }

    // ------------------------------------------------------------------
    // children
    // ------------------------------------------------------------------

    onChildChange(alias: string, child: QlBuilderContainerNode): void {
        if (child.kind === 'join') {
            this.emitNode(QlBuilderTreeOps.replaceChild(this.node, alias, child));
        }
    }

    removeChild(alias: string): void {
        if (this.sideCarDraft?.alias === alias) {
            this.cancelSideCarDraft();
        }
        this.emitNode(QlBuilderTreeOps.replaceChild(this.node, alias, null));
    }

    // ------------------------------------------------------------------
    // "Verknüpfung hinzufügen" dialog
    // ------------------------------------------------------------------

    /** Dialog header: the entity whose relations are offered (join node: the target entity, not the join label). */
    get joinDialogHeader(): string {
        return `Verknüpfung hinzufügen – ${this.meta?.entityLabel || this.entityAlias || this.headerTitle}`;
    }

    /** The active category of the dialog, `null` before the sections exist. */
    get activeJoinSection(): QlBuilderJoinMenuSection | null {
        return this.joinDialogSections.find(section => section.key === this.joinCategory) ?? null;
    }

    openJoinDialog(): void {
        if (this.addChildBlockedReason) {
            return;
        }
        this.joinSearch = '';
        this.joinCategory = 'nToOne';
        this.joinDialogSections = QlBuilderNodeComponent.filterSections(this.menuSections, '');
        this.joinDialogVisible = true;
        this.requestTargetMetas();
    }

    /** Close icon, Escape, "Schließen" — also cancels a side-car flow that is still open. */
    closeJoinDialog(): void {
        this.joinDialogVisible = false;
        this.joinSearch = '';
        if (this.sideCarDraft) {
            this.draftSubscription?.unsubscribe();
            this.sideCarDraft = null;
        }
    }

    /** `p-dialog` `visibleChange(false)` (close icon, Escape) is a cancel. */
    onJoinDialogVisibleChange(visible: boolean): void {
        if (!visible && this.joinDialogVisible) {
            this.closeJoinDialog();
        }
    }

    onJoinSearchChange(value: string): void {
        this.joinSearch = value ?? '';
        this.joinDialogSections = QlBuilderNodeComponent.filterSections(this.menuSections, this.joinSearch);
    }

    /** Enter in the search field adds the first enabled entry of the active category. */
    onJoinSearchEnter(): void {
        const first = this.activeJoinSection?.items.find(item => !item.disabledReason);
        if (first) {
            this.addFromMenu(first);
        }
    }

    onJoinCategoryChange(key: string | number | undefined): void {
        if (key === 'nToOne' || key === 'oneToN' || key === 'extended' || key === 'sideCar') {
            this.joinCategory = key;
        }
    }

    /** Tab counter: the total, or "<visible> von <total>" while a search narrows the category. */
    sectionCounter(section: QlBuilderJoinMenuSection): string {
        return this.joinSearch.trim() && section.items.length !== section.total
            ? `${section.items.length} von ${section.total}`
            : String(section.total);
    }

    /** Pick from the "Verknüpfung hinzufügen" dialog: a join/extended field is added and the dialog closes; a side-car opens its flow. */
    addFromMenu(item: QlBuilderJoinMenuItem): void {

        if (item.disabledReason) {
            return;
        }

        const alias = QlBuilderTreeOps.nextJoinAlias(this.context.usedAliases);

        if (item.kind === 'regular' && item.join) {
            const child: QlBuilderJoinNode = {
                kind: 'join',
                alias,
                name: item.join.name,
                relation: item.relation ?? 'nToOne',
                joinType: 'left',
                // grouping mode: join fields become GROUP BY columns - start empty; row mode: explicit id
                fields: this.context.groupByMode ? [] : [{name: 'id'}],
                filters: [],
                children: [],
            };
            this.closeJoinDialog();
            this.emitNode(QlBuilderTreeOps.addChild(this.node, child));
            return;
        }

        if (item.kind === 'extended' && item.extendedAttribute) {
            // attributes only — a +/_ join is never offered (spike S2)
            const child: QlBuilderExtendedNode = {
                kind: 'extended',
                alias,
                name: QlBuilderNodeComponent.bareName(item.extendedAttribute.attributeName),
                extendedKind: item.extendedAttribute.extendedConfig ? 'config' : 'service',
                resultKind: 'attribute',
            };
            this.closeJoinDialog();
            this.emitNode(QlBuilderTreeOps.addChild(this.node, child));
            return;
        }

        if (item.kind === 'sideCar' && item.sideCar) {
            this.openSideCarDraft(item.sideCar, alias, null);
        }
    }

    // ------------------------------------------------------------------
    // side-car editor
    // ------------------------------------------------------------------

    /** "Version und Felder ändern" of a side-car card: the dialog opens directly in the side-car flow. */
    editSideCar(child: QlBuilderSideCarNode): void {
        const row = this.sideCarRow(child) ?? {
            instanceAlias: child.instanceAlias,
            label: child.instanceAlias,
            joinName: child.instanceAlias,
            scopeKey: child.scopeKey,
            scopeObjectTypeAlias: '',
            uniqueGuaranteed: true,
        };
        this.joinSearch = '';
        this.joinDialogSections = QlBuilderNodeComponent.filterSections(this.menuSections, '');
        this.joinDialogVisible = true;
        this.openSideCarDraft(row, child.alias, child);
    }

    /** Side-car flow → back to the category list (new side-car only). */
    backToJoinList(): void {
        this.draftSubscription?.unsubscribe();
        this.sideCarDraft = null;
    }

    /** Too many fields picked in the side-car flow (the list box has no selection limit). */
    get sideCarFieldsOverLimit(): boolean {
        return (this.sideCarDraft?.selectedFieldIds.length ?? 0) > QL_BUILDER_POLICY_LIMITS.maxSideCarFields;
    }

    onSideCarScopeChange(scopeId: number | null): void {
        if (!this.sideCarDraft) {
            return;
        }
        this.sideCarDraft = {...this.sideCarDraft, scopeId, fieldOptions: null, selectedFieldIds: []};
        this.loadSideCarFields();
    }

    onSideCarFieldsChange(ids: number[]): void {
        if (this.sideCarDraft) {
            this.sideCarDraft = {...this.sideCarDraft, selectedFieldIds: [...(ids ?? [])]};
        }
    }

    get sideCarDraftApplicable(): boolean {
        const draft = this.sideCarDraft;
        return !!draft && draft.scopeId != null && draft.selectedFieldIds.length > 0
            && draft.selectedFieldIds.length <= QL_BUILDER_POLICY_LIMITS.maxSideCarFields;
    }

    applySideCarDraft(): void {

        const draft = this.sideCarDraft;
        if (!draft || !this.sideCarDraftApplicable) {
            return;
        }

        // only pivotable fields can ever be sent, whatever the picker state
        const allowed = new Set((draft.fieldOptions ?? []).filter(o => !o.disabled).map(o => o.value));
        const typeFieldIds = draft.selectedFieldIds.filter(id => allowed.has(id));

        // display labels travel with the node (N7) - never sent, see QlBuilderSideCarNode
        const fieldLabels: { [typeFieldId: number]: string } = {};
        for (const option of draft.fieldOptions ?? []) {
            if (typeFieldIds.includes(option.value)) {
                fieldLabels[option.value] = option.fieldLabel;
            }
        }
        const scopeLabel = (draft.scopes ?? []).find(scope => scope.id === draft.scopeId)?.label;

        const child: QlBuilderSideCarNode = {
            kind: 'sideCar',
            alias: draft.alias,
            instanceAlias: draft.sideCar.joinName,
            scopeKey: draft.sideCar.scopeKey,
            scopeId: draft.scopeId,
            typeFieldIds,
            ...(scopeLabel ? {scopeLabel} : {}),
            fieldLabels,
        };

        this.sideCarDraft = null;
        this.closeJoinDialog();
        this.emitNode(draft.existing
            ? QlBuilderTreeOps.replaceChild(this.node, draft.alias, child)
            : QlBuilderTreeOps.addChild(this.node, child));
    }

    /** "Abbrechen" of the side-car flow: nothing is added, the dialog closes. */
    cancelSideCarDraft(): void {
        this.draftSubscription?.unsubscribe();
        this.sideCarDraft = null;
        this.closeJoinDialog();
    }

    private openSideCarDraft(sideCar: CoreMetaExternalSideCarDto, alias: string, existing: QlBuilderSideCarNode | null): void {

        this.draftSubscription?.unsubscribe();

        this.sideCarDraft = {
            sideCar,
            alias,
            existing: !!existing,
            scopes: null,
            scopesState: 'loading',
            scopeId: existing?.scopeId ?? null,
            fieldOptions: null,
            fieldsState: 'idle',
            selectedFieldIds: existing ? [...existing.typeFieldIds] : [],
        };

        this.draftSubscription = this.sideCarService.getScopes(sideCar.instanceAlias).subscribe({
            next: scopes => {
                if (!this.sideCarDraft) {
                    return;
                }
                this.sideCarDraft = {...this.sideCarDraft, scopes: scopes ?? [], scopesState: 'ready'};
                if (this.sideCarDraft.scopeId != null) {
                    this.loadSideCarFields();
                }
            },
            error: () => {
                if (this.sideCarDraft) {
                    this.sideCarDraft = {...this.sideCarDraft, scopesState: 'error'};
                }
            },
        });
    }

    private loadSideCarFields(): void {

        const draft = this.sideCarDraft;
        if (!draft || draft.scopeId == null) {
            return;
        }

        this.sideCarDraft = {...draft, fieldsState: 'loading'};

        this.draftSubscription?.unsubscribe();
        this.draftSubscription = this.sideCarService.getFields(draft.sideCar.instanceAlias, draft.scopeId).subscribe({
            next: fields => {
                if (!this.sideCarDraft) {
                    return;
                }
                const fieldOptions = QlBuilderNodeComponent.toSideCarFieldOptions(fields ?? []);
                const allowed = new Set(fieldOptions.filter(o => !o.disabled).map(o => o.value));
                this.sideCarDraft = {
                    ...this.sideCarDraft,
                    fieldOptions,
                    fieldsState: 'ready',
                    selectedFieldIds: this.sideCarDraft.selectedFieldIds.filter(id => allowed.has(id)),
                };
            },
            error: () => {
                if (this.sideCarDraft) {
                    this.sideCarDraft = {...this.sideCarDraft, fieldsState: 'error'};
                }
            },
        });
    }

    /** `pivotable=false` (Object/Struct/JsonRaw, or an unresolvable `dataType: null`) → disabled with the reason. */
    static toSideCarFieldOptions(fields: CoreMetaExternalSideCarFieldDto[]): QlBuilderSideCarFieldOption[] {
        return fields.map(field => {
            const pivotable = field.pivotable === true && field.dataType != null;
            return {
                label: field.dataType ? `${field.label} (${field.dataType})` : field.label,
                fieldLabel: field.label,
                value: field.typeFieldId,
                disabled: !pivotable,
                reason: pivotable ? null : QlBuilderNodeComponent.TEXT_NOT_PIVOTABLE,
            };
        });
    }

    // ------------------------------------------------------------------
    // filters (mvs-conditional-builder-entity, fields prefixed with the node alias)
    // ------------------------------------------------------------------

    /**
     * The dialog edits every filter EXCEPT the relative date ones: it cannot represent a period
     * (`${D-TODAY-P30D}` would be read back as a fixed day) — those stay chips and survive save/delete.
     */
    openFilterDialog(): void {
        if (!this.entityAlias) {
            return;
        }
        this.filterDialogCriteria = this.toDialogCriteria(this.dialogFilters().filter(c => !this.isForeignCriterion(c)));
        this.filterDialogWidgetData = QlBuilderNodeComponent.createFilterWidgetData(this.entityAlias);
        this.filterDraft = null;
        this.filterDialogVisible = true;
    }

    /** `mvs-condition-builder` reports every edit with its validity — "Übernehmen" is gated on it. */
    onFilterChange(event: FilterCriteriaChangeEvent): void {
        this.filterDraft = {criteria: event?.filterCriteria ?? [], valid: !!event?.valid};
    }

    get filterDialogApplicable(): boolean {
        return !!this.filterDraft?.valid;
    }

    /** "Übernehmen": the dialog criteria replace the node's dialog filters; relative date chips survive. */
    applyFilterDialog(): void {
        if (!this.filterDialogApplicable) {
            return;
        }
        this.onFilterSave(this.filterDraft.criteria);
    }

    /** The dialog never saw a foreign-alias criterion (see {@link isForeignCriterion}) — it survives a save untouched. */
    onFilterSave(criteria: FilterCriteria[]): void {
        this.closeFilterDialog();
        const foreign = this.dialogFilters().filter(c => this.isForeignCriterion(c));
        this.emitNode({...this.node, filters: [...this.fromDialogCriteria(criteria ?? []), ...foreign, ...this.relativeFilters()]});
    }

    onFilterDelete(): void {
        this.closeFilterDialog();
        this.emitNode({...this.node, filters: this.relativeFilters()});
    }

    onFilterCancel(): void {
        this.closeFilterDialog();
    }

    /** `p-dialog` `visibleChange(false)` (close icon, Escape) is a cancel. */
    onFilterDialogVisibleChange(visible: boolean): void {
        if (!visible && this.filterDialogVisible) {
            this.onFilterCancel();
        }
    }

    private closeFilterDialog(): void {
        this.filterDialogVisible = false;
        this.filterDialogWidgetData = null;
        this.filterDraft = null;
    }

    /** Entity-sourced `WidgetData` for the hosted condition builder (see {@link filterDialogWidgetData}). */
    static createFilterWidgetData(entityAlias: string): WidgetData {
        const widgetData = new WidgetData();
        widgetData.dataSource = 'entity';
        widgetData.dataProvider = 'list';
        widgetData.dataProviderObject = entityAlias;
        return widgetData;
    }

    // ------------------------------------------------------------------
    // relative date filters ("Relativer Zeitraum", `${D-…}` on date fields)
    // ------------------------------------------------------------------

    openRelativeDate(): void {
        if (this.dateFieldOptions.length) {
            this.relativeDateDraft = {index: null, criterion: null};
        }
    }

    editRelativeFilter(index: number): void {
        const criterion = this.node?.filters?.[index];
        if (criterion && this.relativeFilterChips.some(chip => chip.index === index && chip.editable)) {
            this.relativeDateDraft = {index, criterion};
        }
    }

    onRelativeDateApply(criterion: FilterCriteria): void {
        const draft = this.relativeDateDraft;
        if (!draft || !criterion) {
            return;
        }
        const filters = [...(this.node.filters ?? [])];
        if (draft.index != null && draft.index < filters.length) {
            filters[draft.index] = criterion;
        } else {
            filters.push(criterion);
        }
        this.relativeDateDraft = null;
        this.emitNode({...this.node, filters});
    }

    cancelRelativeDate(): void {
        this.relativeDateDraft = null;
    }

    removeRelativeFilter(index: number): void {
        if (this.relativeDateDraft?.index === index) {
            this.relativeDateDraft = null;
        }
        this.emitNode({...this.node, filters: (this.node.filters ?? []).filter((_, i) => i !== index)});
    }

    /**
     * A criterion (or group) that names a field of ANOTHER node of the tree (`j1.name` on the root, `e.status`
     * on a join — a hand-edited definition, or a query-level condition that landed on the root before the
     * builder knew conditions). The node dialog must neither show it (the entity-mode condition builder has no
     * such field and fails on it) nor ever re-prefix it to `e.j1.name`; the panel moves root ones to the
     * query-level conditions ({@link QlBuilderTreeOps.normalizeRootFilters}).
     */
    isForeignCriterion(criterion: FilterCriteria): boolean {
        const own = this.node?.alias;
        const known = new Set(this.context?.usedAliases ?? []);
        return QlBuilderQueryConverter.leafCriteria(criterion).some(leaf => {
            const alias = QlBuilderQueryConverter.filterAliasOf(leaf.field);
            return !!alias && alias !== own && known.has(alias);
        });
    }

    /**
     * Dialog criteria → node filters (N4/N5):
     * - field names get the node alias prefix (generic attribute → `g_<id>:value`); a field that already
     *   carries the prefix of ANOTHER known node alias is left as it is (never `e.j1.name`);
     * - the condition builder's field-less wrapper around its rows is dropped: the node stores a FLAT list of
     *   AND-linked conditions; a group of ONE condition is unwrapped, only a real OR group (two or more
     *   alternatives) stays a group node;
     * - an absolute day picked for an `Instant`/`LocalDateTime` field is normalised to whole days
     *   ({@link normalizeDayCriterion}) - the picker otherwise sends the current time of day.
     */
    fromDialogCriteria(criteria: FilterCriteria[]): FilterCriteria[] {
        const clone = FilterCriteria.cloneFilterCriteriaArray(criteria) ?? [];
        const alias = this.node.alias;
        const genericIdByAttribute = this.genericIdByAttributeName();
        const foreignAliases = new Set((this.context?.usedAliases ?? []).filter(a => a !== alias));
        const walk = (list: FilterCriteria[] | undefined): void => {
            for (const c of list ?? []) {
                const existing = QlBuilderQueryConverter.filterAliasOf(c.field);
                if (c.field && !c.field.startsWith(alias + '.') && !(existing && foreignAliases.has(existing))) {
                    const genericId = genericIdByAttribute.get(c.field);
                    const name = genericId != null ? `g_${genericId}:value` : c.field;
                    c.field = `${alias}.${name}`;
                }
                walk(c.and);
                walk(c.or);
            }
        };
        walk(clone);
        const prefix = `${alias}.`;
        const normalizeLeaf = (leaf: FilterCriteria): FilterCriteria[] => {
            const bare = leaf.field.startsWith(prefix) ? leaf.field.substring(prefix.length) : leaf.field;
            return QlBuilderNodeComponent.normalizeDayCriterion(leaf, this.dateTimeKind(bare));
        };
        return clone.flatMap(c => QlBuilderNodeComponent.toAndList(c, normalizeLeaf));
    }

    /**
     * Node filters → dialog criteria: prefix stripped, `g_<id>:value` → the meta attribute name, day ranges
     * folded back into the single condition the user picked, and the flat list wrapped in an AND group -
     * the condition builder reads a bare list as OR-linked and links a fresh dialog's rows with OR; the
     * wrapper (with one placeholder row when there is nothing yet) makes AND the default.
     */
    toDialogCriteria(filters: FilterCriteria[]): FilterCriteria[] {
        const clone = FilterCriteria.cloneFilterCriteriaArray(filters) ?? [];
        const prefix = this.node.alias + '.';
        const attributeByGenericId = new Map<string, string>(
            Array.from(this.genericIdByAttributeName().entries()).map(([attribute, id]) => [String(id), attribute]));
        const walk = (list: FilterCriteria[] | undefined): void => {
            for (const c of list ?? []) {
                if (c.field?.startsWith(prefix)) {
                    const bare = c.field.substring(prefix.length);
                    const generic = /^g_(\d+):value$/.exec(bare);
                    c.field = generic && attributeByGenericId.has(generic[1]) ? attributeByGenericId.get(generic[1]) : bare;
                }
                walk(c.and);
                walk(c.or);
            }
        };
        walk(clone);
        const rows = QlBuilderNodeComponent.foldDayRanges(clone);
        const wrapped = [QlBuilderNodeComponent.group('and', rows.length ? rows : [FilterCriteria.create('', FilterCriteria.cOperatorEqual, null)])];
        // the condition builder reads `criterion.and.length` / `.or.length` unguarded on every node it converts
        QlBuilderNodeComponent.ensureGroupArrays(wrapped);
        return wrapped;
    }

    /** `and`/`or` as (possibly empty) arrays on every node of the tree - the shape `mvs-condition-builder` expects. */
    static ensureGroupArrays(list: FilterCriteria[]): void {
        for (const c of list) {
            c.and = c.and ?? [];
            c.or = c.or ?? [];
            QlBuilderNodeComponent.ensureGroupArrays(c.and);
            QlBuilderNodeComponent.ensureGroupArrays(c.or);
        }
    }

    /** `Instant`/`LocalDateTime` attributes get whole-day filters; `LocalDate` and everything else stay untouched. */
    private dateTimeKind(bareName: string): 'instant' | 'dateTime' | null {
        const attribute = this.meta?.attributes?.find(a => QlBuilderNodeComponent.scalarFieldName(a) === bareName || a.attributeName === bareName);
        const dataType = attribute?.dataType ?? '';
        if (/Instant/.test(dataType)) {
            return 'instant';
        }
        return /LocalDateTime/.test(dataType) ? 'dateTime' : null;
    }

    /** German label of a filter field (bare name): the attribute label, a generic field's label, else the name. */
    private filterFieldLabel(bareName: string): string {
        const attribute = this.meta?.attributes?.find(a => a.attributeName === bareName || QlBuilderNodeComponent.scalarFieldName(a) === bareName);
        return attribute?.label || QlBuilderKeyLabels.memberLabel(this.meta, bareName) || bareName;
    }

    /**
     * Enum values read as their value-list label, booleans as ja/nein, dates as `dd.MM.yyyy`; everything else
     * verbatim. A multi-value (`IN`/`INN`: an array, or the comma list `5,7`) is resolved element by element
     * ("Abgeschlossen, Abgebrochen").
     */
    private filterValueLabel(bareName: string, value: unknown): string {
        const attribute = this.meta?.attributes?.find(a => a.attributeName === bareName || QlBuilderNodeComponent.scalarFieldName(a) === bareName);
        const labelOf = (single: unknown): string | null =>
            attribute?.valueListEntries?.find(e => String(e.key) === String(single).trim())?.label || null;
        if (Array.isArray(value)) {
            return value.map(v => labelOf(v) ?? QlBuilderNodeComponent.formatFilterValue(v)).join(', ');
        }
        const single = labelOf(value);
        if (single) {
            return single;
        }
        if (typeof value === 'string' && value.includes(',') && attribute?.valueListEntries?.length) {
            const parts = value.split(',');
            const labels = parts.map(labelOf);
            if (labels.every(l => !!l)) {
                return labels.join(', ');
            }
        }
        return QlBuilderNodeComponent.formatFilterValue(value);
    }

    // ------------------------------------------------------------------
    // internals
    // ------------------------------------------------------------------

    private emitNode(node: QlBuilderContainerNode): void {
        this.nodeChange.emit(node);
    }

    private loadMeta(): void {

        this.metaSubscription?.unsubscribe();
        this.sideCarSubscription?.unsubscribe();
        this.loadedAlias = this.entityAlias;
        this.meta = null;
        this.sideCars = null;

        if (!this.entityAlias) {
            this.metaState = 'none';
            this.sideCarsState = 'none';
            this.recompute();
            return;
        }

        this.metaState = 'loading';
        this.sideCarsState = 'loading';

        this.metaSubscription = this.metaService.getByAlias(this.entityAlias, true, true, true, true, true).subscribe({
            next: meta => {
                this.meta = meta;
                this.metaState = 'ready';
                this.recompute();
            },
            error: () => {
                this.metaState = 'error';
                this.recompute();
            },
        });

        this.sideCarSubscription = this.sideCarService.getSideCarsForHost(this.entityAlias).subscribe({
            next: rows => {
                this.sideCars = rows ?? [];
                this.sideCarsState = 'ready';
                this.recompute();
            },
            error: () => {
                this.sideCars = [];
                this.sideCarsState = 'error';
                this.recompute();
            },
        });
    }

    /** Recompute every template-facing array from node + meta + context (never in the template). */
    private recompute(): void {
        if (!this.node || !this.context) {
            return;
        }
        this.childContext = QlBuilderTreeOps.childContext(this.context);
        this.recomputeFieldOptions();
        this.recomputeRelativeFilterChips();
        this.recomputeChildInfo();
        this.recomputeSideCarInfo();
        this.menuSections = this.buildMenuSections();
        this.joinDialogSections = QlBuilderNodeComponent.filterSections(this.menuSections, this.joinSearch);
    }

    /**
     * Loads the entity labels of every join target whose wire label is only its alias (every reverse join)
     * in ONE `MetaService.list` request (labels only: no attributes, no joins - cr.Customer has ~60 targets,
     * and a per-entity joins load surfaced a backend 500 for `wf.WfProcess` as a toast). Requested once per
     * alias and component; after the answer the menu and the child titles are rebuilt with the labels.
     */
    private requestTargetMetas(): void {
        const wanted: string[] = [];
        for (const join of this.meta?.joins ?? []) {
            const alias = join.joinObjectTypeAlias;
            if (alias && QlBuilderNodeComponent.needsTargetMeta(join) && !this.targetMetaRequested.has(alias)) {
                this.targetMetaRequested.add(alias);
                wanted.push(alias);
            }
        }
        if (!wanted.length) {
            return;
        }
        const request = new MetaEntityRequestDto();
        request.listByAliases = wanted;
        request.extractAttributes = false;
        request.extractJoins = false;
        request.extractGenericTypes = false;
        this.targetMetaSubscriptions.add(
            this.metaService.list(request).pipe(catchError(() => of(null))).subscribe(response => {
                const byAlias = new Map((response?.entities ?? []).map(entity => [entity.objectTypeAlias, entity]));
                for (const alias of wanted) {
                    this.targetMetaCache.set(alias, byAlias.get(alias) ?? null);
                }
                this.recompute();
            })
        );
    }

    /** A join whose readable label must come from the target entity: a reverse join, or a label that only repeats the alias. */
    static needsTargetMeta(join: MetaDataJoinDto): boolean {
        return QlBuilderNodeComponent.isReverseJoin(join) || !join.label || join.label === join.joinObjectTypeAlias;
    }

    /**
     * Readable label of a join: its wire label unless that only repeats the target alias (every reverse join),
     * then the target's `entityLabel` from {@link targetMetaCache}, then the alias — never the alias twice.
     */
    private joinLabel(join: MetaDataJoinDto): string {
        const alias = join.joinObjectTypeAlias ?? null;
        if (join.label && join.label !== alias && join.label !== join.name) {
            return join.label;
        }
        const target = alias ? this.targetMetaCache.get(alias) : null;
        return target?.entityLabel || alias || join.label || join.name;
    }

    /**
     * "über Feld <fk>" of a reverse join `<alias>#<fk>`: the FK attribute of the target that points back here.
     * The technical name on purpose - it tells two reverse joins of the same target apart (`#father` / `#mother`),
     * and its label would need the target's joins (see {@link requestTargetMetas}).
     */
    static joinRelationHint(join: MetaDataJoinDto): string | null {
        return QlBuilderNodeComponent.isReverseJoin(join) ? `über Feld ${join.name.split('#')[1]}` : null;
    }

    /** Sections with the search applied: every whitespace-separated token must occur in the entry's search text. */
    static filterSections(sections: QlBuilderJoinMenuSection[], search: string): QlBuilderJoinMenuSection[] {
        const tokens = (search ?? '').toLowerCase().split(/\s+/).filter(token => !!token);
        if (!tokens.length) {
            return sections;
        }
        return sections.map(section => {
            const items = section.items.filter(item => tokens.every(token => item.searchText.includes(token)));
            return {
                ...section,
                items,
                emptyText: section.items.length && !items.length ? `Keine Treffer für „${search.trim()}“.` : section.emptyText,
            };
        });
    }

    /**
     * Version + field labels of every side-car child (N7). Labels the model carries win; for a side-car
     * without them (a saved definition) the discovery is asked once per version - after the discovery rows
     * of this host are in, because the service wants the instance alias, not the join name.
     */
    private recomputeSideCarInfo(): void {
        const info: { [alias: string]: QlBuilderSideCarInfo } = {};
        for (const child of this.sideCarChildren()) {
            const key = QlBuilderKeyLabels.sideCarKey(child.instanceAlias, child.scopeId);
            const cached = this.sideCarLabelCache.get(key);
            const complete = !!child.scopeLabel && child.typeFieldIds.every(id => !!child.fieldLabels?.[id]);
            const loading = !complete && !cached && this.requestSideCarLabels(child, key);
            info[child.alias] = {
                scopeLabel: child.scopeLabel ?? cached?.scopeLabel ?? null,
                fieldLabels: child.typeFieldIds.map(id => child.fieldLabels?.[id] ?? cached?.fieldLabels.get(id) ?? `Zusatzfeld ${id}`),
                loading,
            };
        }
        this.sideCarInfo = info;
    }

    /** Asks the discovery for the labels of one side-car version (once); `true` while the answer is pending. */
    private requestSideCarLabels(child: QlBuilderSideCarNode, key: string): boolean {
        if (this.sideCarLabelRequested.has(key)) {
            return !this.sideCarLabelCache.has(key);
        }
        if (this.sideCarsState === 'loading') {
            return true; // the instance alias is not known yet - retried by the recompute the discovery triggers
        }
        const instanceAlias = this.sideCarRow(child)?.instanceAlias ?? child.instanceAlias;
        this.sideCarLabelRequested.add(key);
        this.sideCarLabelSubscriptions.add(
            forkJoin([
                this.sideCarService.getScopes(instanceAlias).pipe(catchError(() => of(null))),
                this.sideCarService.getFields(instanceAlias, child.scopeId).pipe(catchError(() => of(null))),
            ]).subscribe(([scopes, fields]) => {
                this.sideCarLabelCache.set(key, {
                    scopeLabel: (scopes ?? []).find(scope => scope.id === child.scopeId)?.label ?? null,
                    fieldLabels: new Map((fields ?? []).map(field => [field.typeFieldId, field.label])),
                });
                this.recompute();
            })
        );
        return true;
    }

    private recomputeFieldOptions(): void {

        const attributes = this.meta?.attributes ?? [];
        const genericTypeByAttribute = this.genericTypeNameByAttribute();
        const groupBy = !!this.context.groupByMode;

        const regular: QlBuilderFieldOption[] = [];
        const generic: QlBuilderFieldOption[] = [];
        const eav: QlBuilderFieldOption[] = [];
        const dates: QlBuilderFieldOption[] = [];

        for (const attribute of attributes) {
            if (attribute.extendedService || attribute.extendedConfig) {
                continue; // computed members live in "Erweiterte Felder (berechnet)"
            }
            const label = attribute.label || attribute.attributeName;
            if (QlBuilderRelativeDate.isDateDataType(attribute.dataType)) {
                dates.push({label, value: QlBuilderNodeComponent.scalarFieldName(attribute), group: null});
            }
            // attributeType arrives as the enum ORDINAL on /meta/byAlias - never compare it here
            if (MetaDataAttributeKind.isGenericAttribute(attribute)) {
                generic.push({
                    label,
                    value: MetaDataAttributeKind.scalarFieldName(attribute),
                    group: genericTypeByAttribute.get(attribute.attributeName) ?? null,
                });
            } else if (MetaDataAttributeKind.isEavAttribute(attribute)) {
                eav.push({label, value: MetaDataAttributeKind.scalarFieldName(attribute), group: null});
            } else {
                regular.push({
                    label: label === attribute.attributeName ? label : `${label} (${attribute.attributeName})`,
                    value: attribute.attributeName,
                    group: null,
                });
            }
        }

        // object-typed generic fields come as generic joins (MetaService.convertJoinFromAttribute)
        let objectGenericsHidden = false;
        for (const join of this.meta?.joins ?? []) {
            if (!join.generic || !join.attributeName) {
                continue;
            }
            const label = join.label || join.attributeName;
            const group = genericTypeByAttribute.get(join.attributeName) ?? null;
            // grouping mode: object-typed generics are not groupable at all — spike S3, every
            // suffix fails incl. :id (objectGenericFieldInGroupBy); only scalar g_<id>:value
            if (groupBy) {
                objectGenericsHidden = true;
                continue;
            }
            const suffixes: { suffix: string; text: string }[] =
                [{suffix: 'name', text: 'Name'}, {suffix: 'shortName', text: 'Kurzname'}, {suffix: 'id', text: 'ID'}];
            for (const s of suffixes) {
                generic.push({label: `${label} – ${s.text}`, value: `g_${join.attributeName}:${s.suffix}`, group});
            }
        }

        const selected: { [s in QlBuilderFieldSection]: string[] } = {regular: [], generic: [], eav: []};
        for (const field of this.node.fields ?? []) {
            selected[QlBuilderNodeComponent.sectionOf(field.name)].push(field.name);
        }

        // keep selected-but-unknown names visible (e.g. imported from JSON) instead of dropping them
        const ensure = (options: QlBuilderFieldOption[], names: string[]): QlBuilderFieldOption[] => {
            const known = new Set(options.map(o => o.value));
            const unknown = names.filter(n => !known.has(n)).map(n => ({label: `${n} (unbekannt)`, value: n, group: null}));
            return [...options, ...unknown];
        };

        this.regularOptions = ensure(regular, selected.regular);
        this.genericOptions = ensure(generic, selected.generic);
        this.eavOptions = ensure(eav, selected.eav);
        this.dateFieldOptions = dates;
        this.selected = selected;
        this.objectGenericsHiddenByGroupBy = objectGenericsHidden;
    }

    private recomputeRelativeFilterChips(): void {
        const offeredBases = new Set(QlBuilderRelativeDate.BASE_OPTIONS.map(option => option.value));
        const prefix = `${this.node.alias}.`;
        const chips: QlBuilderRelativeFilterChip[] = [];
        (this.node.filters ?? []).forEach((criterion, index) => {
            if (!QlBuilderRelativeDate.isRelativeCriterion(criterion)) {
                return;
            }
            const bare = criterion.field.startsWith(prefix) ? criterion.field.substring(prefix.length) : criterion.field;
            const option = this.dateFieldOptions.find(o => o.value === bare);
            const expression = QlBuilderRelativeDate.parse(criterion.value);
            chips.push({
                index,
                label: QlBuilderRelativeDate.describeCriterion(criterion, option?.label ?? bare) ?? String(criterion.value),
                value: String(criterion.value),
                editable: !!option && !!expression && offeredBases.has(expression.base)
                    && !!QlBuilderRelativeDate.toAmount(expression),
            });
        });
        this.relativeFilterChips = chips;
    }

    /** The node's relative date filters (chips), in filter order. */
    private relativeFilters(): FilterCriteria[] {
        return (this.node?.filters ?? []).filter(c => QlBuilderRelativeDate.isRelativeCriterion(c));
    }

    /** Every other filter — the ones the filter dialog edits. */
    private dialogFilters(): FilterCriteria[] {
        return (this.node?.filters ?? []).filter(c => !QlBuilderRelativeDate.isRelativeCriterion(c));
    }

    private recomputeChildInfo(): void {
        const info: { [alias: string]: QlBuilderChildInfo } = {};
        for (const child of this.joinChildren()) {
            const metaJoin = this.meta?.joins?.find(j => j.name === child.name);
            const entityAlias = metaJoin?.joinObjectTypeAlias
                || (child.name.includes('#') ? child.name.split('#')[0] : null);
            // a reverse join child reads as its target entity once that label is known (same cache as the dialog)
            info[child.alias] = {entityAlias, title: metaJoin ? this.joinLabel(metaJoin) : child.name};
        }
        this.childInfo = info;
    }

    private buildMenuSections(): QlBuilderJoinMenuSection[] {

        const blocked = this.addChildBlockedReason;
        const groupBy = !!this.context.groupByMode;
        // `generic` alone does NOT identify an object-typed generic field: the backend flags every REVERSE join
        // (`<alias>#<fk>`, MetaService.buildReverseJoinIndexForTenant) `generic: true` as well - see
        // isGenericFieldJoin. Filtering on the bare flag emptied "Zugehörige (1:n)" for every entity (QA #17).
        const joins = (this.meta?.joins ?? []).filter(join =>
            !QlBuilderNodeComponent.isGenericFieldJoin(join)
            && !QlBuilderNodeComponent.isPolymorphicJoin(join));
        const loadingMeta = this.metaState === 'loading';

        const regularItem = (join: MetaDataJoinDto, relation: QlBuilderJoinRelation): QlBuilderJoinMenuItem => {
            const label = this.joinLabel(join);
            const secondary = join.joinObjectTypeAlias ?? join.name;
            const relationHint = QlBuilderNodeComponent.joinRelationHint(join);
            return {
                key: `${relation}:${join.name}`,
                label,
                secondary,
                relationHint,
                warning: null,
                disabledReason: blocked,
                searchText: QlBuilderNodeComponent.searchText(label, secondary, join.name, join.label, relationHint),
                kind: 'regular',
                relation,
                join,
            };
        };

        const relationOf = QlBuilderNodeComponent.joinRelationOf;

        const nToOne = joins
            .filter(j => !j.extendedService && !j.extendedConfig)
            .filter(j => relationOf(j) === MetaDataJoinRelationEnum.manyToOne
                || relationOf(j) === MetaDataJoinRelationEnum.oneToOne)
            .map(j => regularItem(j, 'nToOne'));

        const oneToN = joins
            .filter(j => !j.extendedService && !j.extendedConfig)
            .filter(j => relationOf(j) === MetaDataJoinRelationEnum.oneToMany
                || relationOf(j) === MetaDataJoinRelationEnum.manyToMany)
            .map(j => regularItem(j, relationOf(j) === MetaDataJoinRelationEnum.oneToMany ? 'oneToN' : 'manyToN'));

        const presentExtended = new Set(this.extendedChildren().map(c => c.name));
        const extendedReason = (name: string): string | null => {
            if (groupBy) {
                return QlBuilderNodeComponent.TEXT_EXTENDED_GROUP_BY;
            }
            if (blocked) {
                return blocked;
            }
            if ((this.context.sideCarCount ?? 0) > 0) {
                return QlBuilderNodeComponent.TEXT_EXTENDED_WITH_SIDE_CAR;
            }
            if (this.context.extendedCount >= QL_BUILDER_POLICY_LIMITS.maxExtendedNodes) {
                return QlBuilderNodeComponent.TEXT_EXTENDED_MAX;
            }
            if (presentExtended.has(name)) {
                return 'Bereits hinzugefügt.';
            }
            return null;
        };

        // attributes only: a +/_ JOIN merges as a raw, label-less object and cannot be shown as a
        // cell (spike S2) — extended joins are not offered at all
        const extended: QlBuilderJoinMenuItem[] = (this.meta?.attributes ?? [])
            .filter(a => a.extendedService || a.extendedConfig)
            .map(a => {
                const label = a.label || a.attributeName;
                const secondary = `${a.extendedConfig ? '_' : '+'}${QlBuilderNodeComponent.bareName(a.attributeName)} · Wert`;
                return {
                    key: `extendedAttribute:${a.attributeName}`,
                    label,
                    secondary,
                    relationHint: null,
                    warning: null,
                    disabledReason: extendedReason(QlBuilderNodeComponent.bareName(a.attributeName)),
                    searchText: QlBuilderNodeComponent.searchText(label, secondary, a.attributeName),
                    kind: 'extended' as const,
                    extendedAttribute: a,
                };
            });

        const presentSideCars = new Set(this.sideCarChildren().map(c => c.instanceAlias));
        const sideCarReason = (row: CoreMetaExternalSideCarDto): string | null => {
            if (groupBy) {
                return QlBuilderNodeComponent.TEXT_SIDE_CAR_GROUP_BY;
            }
            if (blocked) {
                return blocked;
            }
            if (this.context.extendedCount > 0) {
                return QlBuilderNodeComponent.TEXT_SIDE_CAR_WITH_EXTENDED;
            }
            return presentSideCars.has(row.joinName) ? 'Bereits hinzugefügt.' : null;
        };
        const sideCar: QlBuilderJoinMenuItem[] = (this.sideCars ?? []).map(row => {
            const label = row.label || row.instanceAlias;
            return {
                key: `sideCar:${row.instanceAlias}`,
                label,
                secondary: row.instanceAlias,
                relationHint: null,
                warning: row.uniqueGuaranteed === false ? 'Werte bei Duplikaten nicht eindeutig' : null,
                disabledReason: sideCarReason(row),
                searchText: QlBuilderNodeComponent.searchText(label, row.instanceAlias, row.joinName),
                kind: 'sideCar' as const,
                sideCar: row,
            };
        });

        return [
            {
                key: 'nToOne',
                title: 'Verknüpft (n:1)',
                hint: 'Verknüpftes Objekt je Datensatz – vervielfacht keine Zeilen.',
                hintSeverity: 'info',
                blockedReason: null,
                emptyText: 'Keine n:1-Verknüpfungen vorhanden.',
                loading: loadingMeta,
                items: nToOne,
                total: nToOne.length,
            },
            {
                key: 'oneToN',
                title: 'Zugehörige (1:n)',
                hint: 'Kann Zeilen vervielfachen (eine Zeile je zugehörigem Eintrag). '
                    + '„nur erster Eintrag“ und Join-Typ werden nach dem Hinzufügen am Knoten gesetzt.',
                hintSeverity: 'warn',
                blockedReason: null,
                emptyText: 'Keine zugehörigen Einträge vorhanden.',
                loading: loadingMeta,
                items: oneToN,
                total: oneToN.length,
            },
            {
                key: 'extended',
                title: 'Erweiterte Felder (berechnet)',
                hint: 'Pro Zeile berechnet: nur Werte, nicht filter-/sortierbar, höchstens eines je Definition, '
                    + 'nicht mit externen Zusatzfeldern.',
                hintSeverity: 'info',
                blockedReason: groupBy ? QlBuilderNodeComponent.TEXT_EXTENDED_GROUP_BY : null,
                emptyText: 'Diese Entität hat keine erweiterten Felder.',
                loading: loadingMeta,
                items: extended,
                total: extended.length,
            },
            {
                key: 'sideCar',
                title: 'Zusatzfelder (extern)',
                hint: 'Externe Felder je Partei-Version: nicht filter-/gruppierbar, umgebungsgebunden, '
                    + 'nicht mit erweiterten Feldern.',
                hintSeverity: 'info',
                blockedReason: groupBy ? QlBuilderNodeComponent.TEXT_SIDE_CAR_GROUP_BY : null,
                emptyText: this.sideCarsState === 'error'
                    ? 'Zusatzfelder konnten nicht geladen werden.'
                    : 'Für diese Entität sind keine externen Zusatzfelder vorhanden.',
                loading: this.sideCarsState === 'loading',
                items: sideCar,
                total: sideCar.length,
            },
        ];
    }

    /** Lower-case haystack of the dialog search — every part that a user might type (label, alias, join name, hint). */
    static searchText(...parts: (string | null | undefined)[]): string {
        return parts.filter(part => !!part).join(' ').toLowerCase();
    }

    /** meta attribute name → TypeField id of every generic_type_attribute of this node's entity. */
    private genericIdByAttributeName(): Map<string, number | string> {
        const map = new Map<string, number | string>();
        for (const attribute of this.meta?.attributes ?? []) {
            if (MetaDataAttributeKind.isGenericAttribute(attribute)) {
                map.set(attribute.attributeName, MetaDataAttributeKind.genericAttributeIdOf(attribute) ?? attribute.attributeName);
            }
        }
        return map;
    }

    /** meta attribute name → name of the generic type it belongs to (only when `genericTypes` was delivered). */
    private genericTypeNameByAttribute(): Map<string, string> {
        const map = new Map<string, string>();
        for (const type of this.meta?.genericTypes ?? []) {
            for (const attribute of type.attributes ?? []) {
                map.set(attribute.attributeName, type.typeName);
            }
            for (const join of type.joins ?? []) {
                if (join.attributeName) {
                    map.set(join.attributeName, type.typeName);
                }
            }
        }
        return map;
    }

    static sectionOf(fieldName: string): QlBuilderFieldSection {
        if (QlBuilderFieldPatterns.generic.test(fieldName)) {
            return 'generic';
        }
        if (QlBuilderFieldPatterns.eav.test(fieldName)) {
            return 'eav';
        }
        return 'regular';
    }

    /** The QL name of a scalar attribute, as the field sections name it (`g_<id>:value`, `o_<name>:value`, plain). */
    static scalarFieldName(attribute: MetaDataAttributeDto): string {
        return MetaDataAttributeKind.scalarFieldName(attribute);
    }

    /** Meta names of +/_ members may or may not carry their prefix — the model stores them bare. */
    static bareName(name: string | undefined): string {
        return (name ?? '').replace(/^[+_]/, '');
    }

    /**
     * A polymorphic join (signed OUT): the wire-level name shape the policy pattern refuses, plus the wildcard
     * reverse joins of `EntityObjectReference` sources (`<sourceAlias>#objectId`, MetaService
     * .buildReverseJoinIndexForTenant), which the backend refuses after resolving them against the metadata
     * (`UiQlWidgetDefinitionPolicy`, `resolved.polymorphic()`).
     */
    static isPolymorphicJoin(join: MetaDataJoinDto | null | undefined): boolean {
        const name = join?.name ?? '';
        return QlBuilderFieldPatterns.polymorphicJoin.test(name) || /#objectId$/.test(name);
    }

    /** A reverse (1:n / n:m) join: `<sourceAlias>#<fkAttribute>` (MetaService.buildReverseJoinIndexForTenant). */
    static isReverseJoin(join: MetaDataJoinDto | null | undefined): boolean {
        return (join?.name ?? '').includes('#');
    }

    /**
     * The join of an OBJECT-TYPED GENERIC FIELD (MetaService.convertJoinFromAttribute: `generic: true` AND
     * `attributeName` set, never a `#` name) - offered as `g_<id>:name|shortName|id` columns, never as a join.
     * Live `/meta/byAlias/tm.Ticket` (tenantA, 2026-09-24): the 24 reverse joins carry `generic: true` too, with
     * `attributeName: null` - they are real joins and must stay in the menu.
     */
    static isGenericFieldJoin(join: MetaDataJoinDto | null | undefined): boolean {
        return !!join?.generic && !!join.attributeName && !QlBuilderNodeComponent.isReverseJoin(join);
    }

    /**
     * `joinRelationEnum` arrives as the enum ORDINAL on `/meta/byAlias` (`MetaDataJoinRelationEnum` is an
     * `AlphaBaseEnum`, same wire shape as `attributeType` - see `MetaDataAttributeKind`) and as the NAME on the
     * by-id path; this reader accepts both. `null` when absent or unknown.
     */
    static joinRelationOf(join: MetaDataJoinDto | null | undefined): MetaDataJoinRelationEnum | null {
        const raw: unknown = join?.joinRelationEnum;
        if (raw == null) {
            return null;
        }
        if (typeof raw === 'number') {
            return Number.isInteger(raw) && typeof MetaDataJoinRelationEnum[raw] === 'string'
                ? raw as MetaDataJoinRelationEnum
                : null;
        }
        if (typeof raw === 'string') {
            const byName = MetaDataJoinRelationEnum[raw as keyof typeof MetaDataJoinRelationEnum];
            return typeof byName === 'number' ? byName : null;
        }
        return null;
    }

    // ------------------------------------------------------------------
    // filter criteria helpers (pure)
    // ------------------------------------------------------------------

    static readonly OPERATOR_SYMBOLS: Readonly<Record<string, string>> = {
        EQ: '=', NE: '≠', GE: '≥', GT: '>', LE: '≤', LT: '<', BT: 'zwischen', CP: 'enthält', SW: 'beginnt mit',
        EW: 'endet mit', IN: 'in', INN: 'nicht in', IS_NULL: 'ist leer', IS_NOT_NULL: 'ist nicht leer',
    };

    /** Absolute date-time value as the condition builder emits it: `2026-09-01T14:03:11.000000Z` (Instant) or `2026-09-01 14:03:11` (LocalDateTime). */
    private static readonly ABSOLUTE_DATE_TIME = /^(\d{4}-\d{2}-\d{2})([T ])(\d{2}):(\d{2})(?::(\d{2}))?(.*)$/;

    /** A field-less group node in the proven wire shape (`field: ''`), holding `and` or `or` members. */
    static group(kind: 'and' | 'or', members: FilterCriteria[]): FilterCriteria {
        const node = FilterCriteria.create('', '', '');
        if (kind === 'and') {
            node.and = members;
        } else {
            node.or = members;
        }
        return node;
    }

    /** A leaf without the empty `and`/`or` arrays the dialog attaches. */
    private static leaf(c: FilterCriteria): FilterCriteria {
        return FilterCriteria.createFilterWithType(c.field, c.operation, c.value, c.valueTo, c.type ?? null);
    }

    /**
     * One dialog node → the equivalent AND-linked list: leaves (through `normalizeLeaf`), nested AND groups
     * flattened, an OR group of one member unwrapped, a real OR group kept as ONE group node.
     */
    static toAndList(c: FilterCriteria, normalizeLeaf: (leaf: FilterCriteria) => FilterCriteria[] = leaf => [leaf]): FilterCriteria[] {
        if (c.field) {
            return normalizeLeaf(QlBuilderNodeComponent.leaf(c));
        }
        if (c.and?.length) {
            return c.and.flatMap(m => QlBuilderNodeComponent.toAndList(m, normalizeLeaf));
        }
        if (c.or?.length) {
            const members = c.or.flatMap(m => QlBuilderNodeComponent.toOrList(m, normalizeLeaf));
            if (!members.length) {
                return [];
            }
            if (members.length === 1) {
                return members[0].field ? members : (members[0].and ?? []);
            }
            return [QlBuilderNodeComponent.group('or', members)];
        }
        return [];
    }

    /** One dialog node → the equivalent OR-linked list: nested OR groups flattened, an AND group kept as ONE group node. */
    static toOrList(c: FilterCriteria, normalizeLeaf: (leaf: FilterCriteria) => FilterCriteria[]): FilterCriteria[] {
        if (c.field) {
            const normalized = normalizeLeaf(QlBuilderNodeComponent.leaf(c));
            return normalized.length === 1 ? normalized : [QlBuilderNodeComponent.group('and', normalized)];
        }
        if (c.or?.length) {
            return c.or.flatMap(m => QlBuilderNodeComponent.toOrList(m, normalizeLeaf));
        }
        if (c.and?.length) {
            const members = c.and.flatMap(m => QlBuilderNodeComponent.toAndList(m, normalizeLeaf));
            if (!members.length) {
                return [];
            }
            if (members.length === 1) {
                return members[0].field ? members : (members[0].or ?? []);
            }
            return [QlBuilderNodeComponent.group('and', members)];
        }
        return [];
    }

    /**
     * Whole-day semantics for a day picked on a date-TIME field (N5). The picker sends the current time of
     * day, so "= 01.09.2026" would match one second of that day. Returns the AND-linked replacement:
     * `EQ d` → `GE d 00:00` + `LT d+1 00:00`; `NE d` → one OR group `LT d 00:00` | `GE d+1 00:00`;
     * `GE`/`LT d` → the day's midnight; `GT d` → `GE d+1`; `LE d` → `LT d+1`; `BT d1..d2` → `GE d1` + `LT d2+1`.
     * Relative values (`${D-…}`), plain dates and non-date-time fields pass through unchanged.
     *
     * The day is the user's LOCAL calendar day (browser time zone) - a zoned picker value
     * (`2026-09-14T22:30:00Z` = 15.09. 00:30 CEST) is read in local time first. The bounds are that day's
     * local midnights: on an `Instant` as UTC instants (`2026-09-14T22:00:00Z`, DST-correct via `Date`), on a
     * `LocalDateTime` as zone-less wall-clock midnight (`2026-09-15T00:00:00`), which the backend takes verbatim.
     */
    static normalizeDayCriterion(leaf: FilterCriteria, kind: 'instant' | 'dateTime' | null): FilterCriteria[] {
        const parsed = kind ? QlBuilderNodeComponent.parseAbsoluteDateTime(leaf.value) : null;
        if (!parsed) {
            return [leaf];
        }
        const at = (day: string): string => kind === 'instant'
            ? QlBuilderNodeComponent.localMidnightInstant(day)
            : `${day}${parsed.separator}00:00:00`;
        const next = QlBuilderNodeComponent.addDays(parsed.day, 1);
        const make = (operation: string, value: string): FilterCriteria =>
            FilterCriteria.createFilterWithType(leaf.field, operation, value, null, leaf.type ?? null);
        switch (leaf.operation) {
            case FilterCriteria.cOperatorEqual:
                return [make('GE', at(parsed.day)), make('LT', at(next))];
            case FilterCriteria.cOperatorNotEqual:
                return [QlBuilderNodeComponent.group('or', [make('LT', at(parsed.day)), make('GE', at(next))])];
            case FilterCriteria.cOperatorGreaterEqual:
                return [make('GE', at(parsed.day))];
            case FilterCriteria.cOperatorGreaterThan:
                return [make('GE', at(next))];
            case FilterCriteria.cOperatorLowerThan:
                return [make('LT', at(parsed.day))];
            case FilterCriteria.cOperatorLowerEqual:
                return [make('LT', at(next))];
            case FilterCriteria.cOperatorBetween: {
                const to = QlBuilderNodeComponent.parseAbsoluteDateTime(leaf.valueTo);
                return to
                    ? [make('GE', at(parsed.day)), make('LT', at(QlBuilderNodeComponent.addDays(to.day, 1)))]
                    : [leaf];
            }
            default:
                return [leaf];
        }
    }

    /**
     * Inverse of {@link normalizeDayCriterion} for display and for the dialog: `GE d 00:00` + `LT d2 00:00` on
     * the same field → `EQ d` (d2 = d+1) or `BT d..d2-1`; the OR group `LT d` | `GE d+1` → `NE d`. Recurses into
     * groups; everything else is kept.
     */
    static foldDayRanges(list: FilterCriteria[]): FilterCriteria[] {
        const out: FilterCriteria[] = [];
        for (let i = 0; i < list.length; i++) {
            const c = list[i];
            const nextC = list[i + 1];
            const pair = nextC ? QlBuilderNodeComponent.dayPair(c, nextC, 'GE', 'LT') : null;
            if (pair) {
                const at = (day: string): string => QlBuilderNodeComponent.dayBound(day, pair.separator, pair.zoned);
                out.push(pair.consecutive
                    ? FilterCriteria.createFilterWithType(c.field, 'EQ', at(pair.fromDay), null, c.type ?? null)
                    : FilterCriteria.createFilterWithType(c.field, 'BT', at(pair.fromDay), at(QlBuilderNodeComponent.addDays(pair.toDay, -1)), c.type ?? null));
                i++;
                continue;
            }
            if (!c.field && c.or?.length === 2) {
                const ne = QlBuilderNodeComponent.dayPair(c.or[0], c.or[1], 'LT', 'GE');
                if (ne?.consecutive) {
                    out.push(FilterCriteria.createFilterWithType(c.or[0].field, 'NE', QlBuilderNodeComponent.dayBound(ne.fromDay, ne.separator, ne.zoned), null, c.or[0].type ?? null));
                    continue;
                }
            }
            if (!c.field && (c.and?.length || c.or?.length)) {
                const folded = FilterCriteria.create(c.field ?? '', c.operation ?? '', c.value ?? '');
                if (c.and?.length) {
                    folded.and = QlBuilderNodeComponent.foldDayRanges(c.and);
                } else {
                    folded.or = QlBuilderNodeComponent.foldDayRanges(c.or);
                }
                out.push(folded);
                continue;
            }
            out.push(c);
        }
        return out;
    }

    /**
     * Two LOCAL-midnight bounds on the same field with the given operations, in this order; `null` otherwise.
     * Zoned bounds count as midnight when they are local midnight in the browser time zone (what
     * {@link normalizeDayCriterion} writes); both bounds must be of the same shape (zoned or wall-clock).
     */
    private static dayPair(a: FilterCriteria, b: FilterCriteria, firstOp: string, secondOp: string):
        { fromDay: string; toDay: string; consecutive: boolean; separator: string; zoned: boolean } | null {
        if (!a.field || a.field !== b.field || a.operation !== firstOp || b.operation !== secondOp) {
            return null;
        }
        const from = QlBuilderNodeComponent.parseAbsoluteDateTime(a.value);
        const to = QlBuilderNodeComponent.parseAbsoluteDateTime(b.value);
        if (!from || !to || !from.midnight || !to.midnight || from.zoned !== to.zoned || to.day <= from.day) {
            return null;
        }
        // GE|LT (EQ/BT) and LT|GE (NE) both start at a's day and end at b's day
        return {fromDay: from.day, toDay: to.day, consecutive: QlBuilderNodeComponent.addDays(from.day, 1) === to.day, separator: from.separator, zoned: from.zoned};
    }

    /** Midnight of a local day in the shape of the bound it replaces: a UTC instant when zoned, wall-clock otherwise. */
    private static dayBound(day: string, separator: string, zoned: boolean): string {
        return zoned ? QlBuilderNodeComponent.localMidnightInstant(day) : `${day}${separator}00:00:00`;
    }

    /**
     * An absolute date-time value in LOCAL terms: a zoned value (`Z`, `±hh:mm`) is converted to the browser
     * time zone (day, hour, minute of the local wall clock); a zone-less value is taken as wall-clock time.
     * `midnight` = 00:00:00(.000) local.
     */
    private static parseAbsoluteDateTime(value: unknown):
        { day: string; separator: string; zoned: boolean; midnight: boolean; hour: string; minute: string } | null {
        if (typeof value !== 'string') {
            return null;
        }
        const m = QlBuilderNodeComponent.ABSOLUTE_DATE_TIME.exec(value);
        if (!m) {
            return null;
        }
        const tail = m[6] ?? '';
        const zone = /^(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/.exec(tail);
        if (!zone) {
            const fractionZero = !/[1-9]/.test(tail);
            return {day: m[1], separator: m[2], zoned: false, hour: m[3], minute: m[4],
                midnight: m[3] === '00' && m[4] === '00' && (m[5] ?? '00') === '00' && fractionZero};
        }
        const millis = (zone[1] ?? '').padEnd(3, '0').substring(0, 3);
        const offset = zone[2] === 'Z' ? 'Z' : zone[2].replace(/^([+-]\d{2})(\d{2})$/, '$1:$2');
        const date = new Date(`${m[1]}T${m[3]}:${m[4]}:${m[5] ?? '00'}.${millis}${offset}`);
        if (isNaN(date.getTime())) {
            return null;
        }
        const two = (n: number): string => String(n).padStart(2, '0');
        return {
            day: `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`,
            separator: m[2],
            zoned: true,
            hour: two(date.getHours()),
            minute: two(date.getMinutes()),
            midnight: date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0,
        };
    }

    /**
     * Local midnight of `yyyy-MM-dd` (browser time zone) as a UTC instant `yyyy-MM-ddTHH:mm:ssZ` - for Berlin
     * `2026-09-15` → `2026-09-14T22:00:00Z` (CEST), `2026-12-15` → `2026-12-14T23:00:00Z` (CET).
     */
    static localMidnightInstant(day: string): string {
        const [y, mo, d] = day.split('-').map(Number);
        const date = new Date(y, mo - 1, d);
        const two = (n: number): string => String(n).padStart(2, '0');
        return `${date.getUTCFullYear()}-${two(date.getUTCMonth() + 1)}-${two(date.getUTCDate())}`
            + `T${two(date.getUTCHours())}:${two(date.getUTCMinutes())}:${two(date.getUTCSeconds())}Z`;
    }

    /** `yyyy-MM-dd` ± days, computed in UTC so no DST shift can produce a 23-hour day. */
    static addDays(day: string, days: number): string {
        const [y, m, d] = day.split('-').map(Number);
        const date = new Date(Date.UTC(y, m - 1, d + days));
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    }

    /** Dates as `dd.MM.yyyy` (plus `HH:mm` when the time is not midnight), booleans as ja/nein, else the raw value. */
    static formatFilterValue(value: unknown): string {
        if (value == null) {
            return '';
        }
        if (value === true || value === 'true') {
            return 'ja';
        }
        if (value === false || value === 'false') {
            return 'nein';
        }
        const text = String(value);
        const parsed = QlBuilderNodeComponent.parseAbsoluteDateTime(text);
        if (parsed) {
            // local calendar day and wall-clock time (a zoned value is shown in the browser time zone)
            const [y, mo, d] = parsed.day.split('-');
            return parsed.midnight ? `${d}.${mo}.${y}` : `${d}.${mo}.${y} ${parsed.hour}:${parsed.minute}`;
        }
        const plainDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
        if (plainDay) {
            return `${plainDay[3]}.${plainDay[2]}.${plainDay[1]}`;
        }
        const relative = QlBuilderRelativeDate.parse(text);
        return relative ? QlBuilderRelativeDate.describe(relative) : text;
    }

    /**
     * Readable text of one criterion: "Angelegt am ≥ 01.09.2026", "Status ≠ Abgeschlossen"; a group reads as
     * its members in parentheses joined with und/oder.
     */
    static describeCriteria(criteria: FilterCriteria,
                            resolver: { fieldLabel: (field: string) => string; valueLabel: (field: string, value: unknown) => string }
                                = {fieldLabel: f => f, valueLabel: (_f, v) => QlBuilderNodeComponent.formatFilterValue(v)}): string {
        if (!criteria.field) {
            const isAnd = !!criteria.and?.length;
            const parts = (isAnd ? criteria.and : criteria.or ?? []).map(c => QlBuilderNodeComponent.describeCriteria(c, resolver));
            if (!parts.length) {
                return '(Bedingungsgruppe)';
            }
            return parts.length === 1 ? parts[0] : `(${parts.join(isAnd ? ' und ' : ' oder ')})`;
        }
        const symbol = QlBuilderNodeComponent.OPERATOR_SYMBOLS[criteria.operation] ?? criteria.operation;
        const label = resolver.fieldLabel(criteria.field);
        if (criteria.operation === FilterCriteria.cOperatorIsNull || criteria.operation === FilterCriteria.cOperatorIsNotNull) {
            return `${label} ${symbol}`;
        }
        if (criteria.operation === FilterCriteria.cOperatorBetween) {
            return `${label} ${symbol} ${resolver.valueLabel(criteria.field, criteria.value)} und ${resolver.valueLabel(criteria.field, criteria.valueTo)}`;
        }
        const value = criteria.value == null ? '' : ` ${resolver.valueLabel(criteria.field, criteria.value)}`;
        return `${label} ${symbol}${value}`;
    }
}
